import {
  closestCenter,
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  MeasuringStrategy,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  defaultAnimateLayoutChanges,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  Bars3Icon,
  EyeIcon,
  PencilIcon,
  PlusIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import axios from 'axios';
import React, {
  FormEvent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import GroupsManagement from '../components/GroupsManagement';
import { Organization } from '../components/OrganizationSelector';
import PlanLimitDialog from '../components/PlanLimitDialog';
import { CURRENT_CAPABILITIES, PROPOSITION_TYPES } from '../constants/enums';
import { useOrganization } from '../context/OrganizationContext';
import { groupsService } from '../services/groups';
import { Proposition as BaseProposition } from '../services/propositions';
import { OrganizationGroup } from '../types/groups';
import { getAuthHeaders } from '../utils/auth';
import { formatCurrency } from '../utils/currency';
import {
  formatCurrentCapability,
  formatPropositionType,
} from '../utils/enumFormatting';

import { API_URL } from '../constants/urls';

// Extended Proposition type to include additional fields not in the service interface
interface Proposition extends Omit<BaseProposition, 'display_order'> {
  type: string;
  obvious_choice_score?: number | null;
  current_capability?: string;
  booked_business?: number;
  lost_business?: number;
  open_pipeline_unweighted?: number;
  open_pipeline_weighted?: number;
  owner_id?: string;
  owner_name?: string;
  group?: { id: string; name: string; color: string | null } | null;
  prospect_count?: number;
  customer_count?: number;
  testimonial_count?: number;
  case_study_count?: number;
  award_count?: number;
  display_order: number;
}

type PropositionFormData = {
  name: string;
  type: string;
  description: string;
  current_capability: string;
  owner_id: string;
  problems_solved: string[];
  value_delivered: string[];
  jobs_to_be_done: string[];
  competitive_advantages: string[];
  success_metrics: string[];
  group_id: string | null;
};

const INITIAL_FORM_DATA: PropositionFormData = {
  name: '',
  type: '',
  description: '',
  current_capability: '',
  owner_id: '',
  problems_solved: [],
  value_delivered: [],
  jobs_to_be_done: [],
  competitive_advantages: [],
  success_metrics: [],
  group_id: null,
};

// Regular Proposition Row Component (non-draggable)
// (Former) PropositionRow removed: consolidated into inline rendering for clarity

// (Former) SortableMobilePropositionCard removed; mobile uses non-DnD grouped view

// Sortable proposition row component
interface SortablePropositionRowProps {
  proposition: Proposition;
  canEdit: boolean;
  canDelete: boolean;
  reorderMode: boolean;
  onView: () => void;
  onEdit: (prop: Proposition) => void;
  onDelete: (id: string) => void;
  organization: Organization | null;
}

function SortablePropositionRow({
  proposition,
  canEdit,
  canDelete,
  reorderMode,
  onView,
  onEdit,
  onDelete,
  organization,
}: SortablePropositionRowProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: proposition.id,
    animateLayoutChanges: defaultAnimateLayoutChanges,
  });

  // Issue is fixed with MeasuringStrategy.Always on DndContext

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1000 : undefined,
    position: isDragging ? ('relative' as const) : undefined,
  };

  return (
    <tr ref={setNodeRef} style={style} className="hover:bg-gray-50">
      {reorderMode && canEdit && (
        <td className="px-2 py-4">
          <button
            {...attributes}
            {...listeners}
            className="cursor-move text-gray-400 hover:text-gray-600"
          >
            <Bars3Icon className="h-5 w-5" />
          </button>
        </td>
      )}
      <td className="px-6 py-4 whitespace-nowrap">
        <div>
          <div className="flex items-center gap-2">
            {proposition.group?.color && (
              <div
                className="w-3 h-3 rounded-full flex-shrink-0"
                style={{
                  backgroundColor: proposition.group.color,
                }}
              />
            )}
            <button
              onClick={onView}
              className="text-sm font-medium text-gray-900 hover:text-blue-600 text-left"
            >
              {proposition.name}
            </button>
          </div>
          {proposition.owner_name && (
            <p className="text-xs text-gray-500 ml-5">
              {proposition.owner_name}
            </p>
          )}
        </div>
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
        {formatPropositionType(proposition.type) || '-'}
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
        {formatCurrentCapability(proposition.current_capability) || '-'}
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
        {proposition.obvious_choice_score !== null &&
        proposition.obvious_choice_score !== undefined
          ? `${proposition.obvious_choice_score.toFixed(1)}/10`
          : '-'}
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
        <div className="space-y-1">
          <div>
            <span className="text-green-600 font-medium">
              {formatCurrency(
                proposition.booked_business || 0,
                organization?.currency || 'USD',
                { compact: true },
              )}
            </span>
            <span className="text-xs text-gray-500 ml-1">Won</span>
          </div>
          <div>
            <span className="text-blue-600 text-sm">
              {formatCurrency(
                proposition.open_pipeline_weighted || 0,
                organization?.currency || 'USD',
                {
                  compact: true,
                },
              )}
            </span>
            <span className="text-xs text-gray-500 ml-1">Weighted</span>
          </div>
        </div>
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
        <div className="flex gap-3">
          <div className="text-center">
            <div className="font-semibold">
              {proposition.prospect_count || 0}
            </div>
            <div className="text-xs text-gray-500">Prospects</div>
          </div>
          <div className="text-center">
            <div className="font-semibold">
              {proposition.customer_count || 0}
            </div>
            <div className="text-xs text-gray-500">Customers</div>
          </div>
        </div>
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
        <div className="flex justify-end gap-2">
          <button
            onClick={onView}
            className="text-blue-600 hover:text-blue-900"
            title="View"
          >
            <EyeIcon className="h-5 w-5" />
          </button>
          {canEdit && (
            <button
              onClick={() => onEdit(proposition)}
              className="text-yellow-600 hover:text-yellow-900"
              title="Edit"
            >
              <PencilIcon className="h-5 w-5" />
            </button>
          )}
          {canDelete && (
            <button
              onClick={() => onDelete(proposition.id)}
              className="text-red-600 hover:text-red-900"
              title="Delete"
            >
              <TrashIcon className="h-5 w-5" />
            </button>
          )}
        </div>
      </td>
    </tr>
  );
}

export default function PropositionsTailwind() {
  const navigate = useNavigate();
  const { selectedOrg: organization, canEdit, canDelete } = useOrganization();
  const [propositions, setPropositions] = useState<Proposition[]>([]);
  const [open, setOpen] = useState(false);
  const [editingProposition, setEditingProposition] =
    useState<Proposition | null>(null);
  const [formData, setFormData] =
    useState<PropositionFormData>(INITIAL_FORM_DATA);
  const [members, setMembers] = useState<
    Array<{ user_id: string; user_name: string }>
  >([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState<'propositions' | 'groups'>(
    'propositions',
  );
  const [groups, setGroups] = useState<OrganizationGroup[]>([]);
  const [organizationDetails, setOrganizationDetails] = useState<{
    plan_type?: string;
  } | null>(null);
  const [reorderMode, setReorderMode] = useState(false);
  const [originalPropositions, setOriginalPropositions] = useState<
    Proposition[]
  >([]);
  const [hasChanges, setHasChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Plan limit dialog state
  const [planLimitDialog, setPlanLimitDialog] = useState<{
    open: boolean;
    limitType: 'user' | 'editor' | 'proposition' | 'segment';
    currentCount?: number;
    limit?: number;
  }>({ open: false, limitType: 'proposition' });

  // canEdit and canDelete are already from useOrganization hook

  // DnD sensors configuration
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // Require drag of 8px to start
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  // Initial fetch of data (non-callback versions are below for reuse elsewhere)

  // Fetchers
  const fetchPropositionsCb = useCallback(async () => {
    if (!organization) return;
    try {
      const response = await axios.get(
        `${API_URL}/propositions?organization_id=${organization.id}`,
        {
          headers: getAuthHeaders(),
        },
      );
      setPropositions(response.data);
    } catch (_error) {
      // console.error removed
    }
  }, [organization]);

  const fetchMembersCb = useCallback(async () => {
    if (!organization) return;
    try {
      const response = await axios.get(
        `${API_URL}/organizations/${organization.id}/members?organization_id=${organization.id}`,
      );
      setMembers(response.data);
    } catch (_error) {
      // console.error removed
    }
  }, [organization]);

  const fetchGroupsCb = useCallback(async () => {
    if (!organization) return;
    try {
      const groupsData = await groupsService.getGroups(
        organization.id,
        'proposition',
      );
      setGroups(groupsData);
    } catch (_error) {
      // console.error removed
    }
  }, [organization]);

  const fetchAll = useCallback(() => {
    if (!organization) return;
    fetchPropositionsCb();
    fetchMembersCb();
    fetchGroupsCb();
  }, [organization, fetchPropositionsCb, fetchMembersCb, fetchGroupsCb]);

  useEffect(() => {
    if (!organization) return;
    fetchAll();
    // Fetch organization details to get plan_type
    axios
      .get(`${API_URL}/organizations/${organization.id}`)
      .then((res) => {
        setOrganizationDetails(res.data);
      })
      .catch((_error) => {
        // console.error removed
      });
  }, [organization, fetchAll]);

  // (Removed duplicate non-callback fetchers)

  const handleOpen = () => setOpen(true);
  const handleClose = () => {
    setOpen(false);
    setEditingProposition(null);
    setFormData(INITIAL_FORM_DATA);
  };

  const handleEdit = (proposition: Proposition) => {
    setEditingProposition(proposition);
    setOpen(true);
  };

  // Populate form when editing proposition changes
  useEffect(() => {
    if (editingProposition) {
      setFormData({
        name: editingProposition.name || '',
        type: editingProposition.type || '',
        description: editingProposition.description || '',
        current_capability: editingProposition.current_capability || '',
        owner_id: editingProposition.owner_id || '',
        problems_solved: [],
        value_delivered: [],
        jobs_to_be_done: [],
        competitive_advantages: [],
        success_metrics: [],
        group_id: editingProposition.group_id || null,
      });
    }
  }, [editingProposition]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!organization) return;

    try {
      // Clean the form data - convert empty strings to null/undefined for optional fields
      const cleanedData = {
        name: formData.name,
        type: formData.type || undefined,
        description: formData.description || undefined,
        current_capability: formData.current_capability || undefined,
        owner_id: formData.owner_id || undefined,
        // Always include group_id; send null to ungroup when empty or null
        group_id: formData.group_id ? formData.group_id : null,
        problems_solved: formData.problems_solved,
        value_delivered: formData.value_delivered,
        jobs_to_be_done: formData.jobs_to_be_done,
        competitive_advantages: formData.competitive_advantages,
        success_metrics: formData.success_metrics,
      };

      if (editingProposition) {
        await axios.put(
          `${API_URL}/propositions/${editingProposition.id}?organization_id=${organization.id}`,
          cleanedData,
        );
      } else {
        await axios.post(
          `${API_URL}/propositions?organization_id=${organization.id}`,
          cleanedData,
        );
      }
      fetchPropositionsCb();
      handleClose();
    } catch (error: unknown) {
      const errorDetail =
        typeof error === 'object' && error !== null && 'response' in error
          ? // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (error as any).response?.data?.detail
          : undefined;

      // Check if it's a plan limit error
      if (
        typeof errorDetail === 'object' &&
        errorDetail?.error === 'object_limit_exceeded'
      ) {
        setPlanLimitDialog({
          open: true,
          limitType: 'proposition',
          currentCount: errorDetail.current_count,
          limit: errorDetail.limit,
        });
        handleClose(); // Close the creation dialog
      } else {
        // console.error removed
        // You could show a regular error message here
      }
    }
  };

  const handleDelete = async (propositionId: string) => {
    if (
      !organization ||
      !window.confirm('Are you sure you want to delete this proposition?')
    )
      return;

    try {
      await axios.delete(
        `${API_URL}/propositions/${propositionId}?organization_id=${organization.id}`,
      );
      fetchPropositionsCb();
    } catch (error) {
      // console.error removed
    }
  };

  // Stable ordering across modes: first by display_order (if available), then by name
  const orderedPropositions = useMemo(() => {
    const clones = [...propositions];
    clones.sort((a, b) => {
      const aOrder =
        typeof a.display_order === 'number'
          ? a.display_order
          : Number.MAX_SAFE_INTEGER;
      const bOrder =
        typeof b.display_order === 'number'
          ? b.display_order
          : Number.MAX_SAFE_INTEGER;
      if (aOrder !== bOrder) return aOrder - bOrder;
      return a.name.localeCompare(b.name);
    });
    return clones;
  }, [propositions]);

  // Use raw list during reorder to preserve live drag order; otherwise, use stable ordered list
  const listForRender = reorderMode ? propositions : orderedPropositions;

  const filteredPropositions = listForRender.filter((proposition) =>
    proposition.name.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  // Handle drag end for reordering - only updates local state
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over || active.id === over.id) {
      return;
    }

    const activeItem = propositions.find((p) => p.id === active.id);
    const overItem = propositions.find((p) => p.id === over.id);
    if (!activeItem || !overItem) return;
    // Determine valid proposition group names
    const propGroupsMap = new Map(
      groups
        .filter((g) => g.group_type === 'proposition')
        .map((g) => [g.id, g.name]),
    );
    const normalizeGroupName = (p?: Proposition) =>
      p && p.group_id && propGroupsMap.has(p.group_id)
        ? (propGroupsMap.get(p.group_id) as string)
        : 'Ungrouped';
    const activeGroupName = normalizeGroupName(activeItem);
    const overGroupName = normalizeGroupName(overItem);
    // Restrict to same valid proposition group in reorder mode
    if (reorderMode && activeGroupName !== overGroupName) {
      return;
    }

    const groupName = activeGroupName;
    // Use the same source the user is seeing (filteredPropositions), but keep only items of this group
    const groupItems = filteredPropositions.filter(
      (p) => normalizeGroupName(p) === groupName,
    );
    const idsInGroup = groupItems.map((p) => p.id);
    const oldIndex = idsInGroup.indexOf(active.id as string);
    const newIndex = idsInGroup.indexOf(over.id as string);

    if (oldIndex !== -1 && newIndex !== -1) {
      // Reorder within the group only
      const reorderedGroup = arrayMove(groupItems, oldIndex, newIndex);
      // Build new full list with the group replaced by reorderedGroup (use propositions to preserve all items)
      const newList: Proposition[] = [];
      let gi = 0;
      for (const p of propositions) {
        if (normalizeGroupName(p) === groupName) {
          newList.push(reorderedGroup[gi++] ?? p);
        } else {
          newList.push(p);
        }
      }
      // Reindex display_order per group for stability
      const reindexed = newList.map((p) => ({
        ...p,
        display_order:
          normalizeGroupName(p) === groupName
            ? reorderedGroup.findIndex((x) => x.id === p.id)
            : (p.display_order ?? Number.MAX_SAFE_INTEGER),
      }));
      setPropositions(reindexed);
      setHasChanges(true);
    }
  };

  // Enter reorder mode and save original state
  const handleEnterReorderMode = () => {
    // Clear search when entering reorder mode
    setSearchTerm('');
    // Assign proper display_order values based on current order
    const propsWithOrder = propositions.map((prop, index) => ({
      ...prop,
      display_order: index,
    }));
    setPropositions(propsWithOrder);
    setOriginalPropositions([...propsWithOrder]);
    setReorderMode(true);
    setHasChanges(false);
  };

  // Save the new order to the backend
  const handleSaveOrder = async () => {
    if (!organization || !hasChanges) return;

    setIsSaving(true);

    // Prepare update for API
    const updates = propositions.map((prop, index) => ({
      id: prop.id,
      display_order: index,
      group_name: prop.group_name || null,
      group_id: prop.group_id || null,
    }));

    try {
      const response = await axios.put(
        `${API_URL}/propositions/update-order?organization_id=${organization.id}`,
        { items: updates },
        {
          headers: getAuthHeaders(),
        },
      );

      // Update with server response
      setPropositions(response.data);
      setReorderMode(false);
      setHasChanges(false);
      setOriginalPropositions([]);
    } catch (_error) {
      alert('Failed to save the new order. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  // Cancel reordering and restore original state
  const handleCancelReorder = () => {
    setPropositions(originalPropositions);
    setReorderMode(false);
    setHasChanges(false);
    setOriginalPropositions([]);
  };

  // Group propositions by proposition groups only; fall back to 'Ungrouped' when group_id not a proposition group
  const propGroupNamesById = useMemo(() => {
    const m = new Map<string, string>();
    groups
      .filter((g) => g.group_type === 'proposition')
      .forEach((g) => m.set(g.id, g.name));
    return m;
  }, [groups]);

  const groupedPropositions = filteredPropositions.reduce(
    (acc, prop) => {
      const group =
        (prop.group_id && propGroupNamesById.get(prop.group_id)) || 'Ungrouped';
      if (!acc[group]) acc[group] = [];
      acc[group].push(prop);
      return acc;
    },
    {} as Record<string, Proposition[]>,
  );

  // Ensure grouping uses group display order, Ungrouped last
  const sortedGroupEntries = React.useMemo(() => {
    const orderIndex = new Map<string, number>();
    groups
      .filter((g) => g.group_type === 'proposition')
      .forEach((g, i) =>
        orderIndex.set(
          g.name,
          g.name === 'Ungrouped'
            ? Number.MAX_SAFE_INTEGER
            : (g.display_order ?? i),
        ),
      );
    const entries = Object.entries(groupedPropositions);
    entries.sort(([a], [b]) => {
      const aOrder = orderIndex.has(a)
        ? (orderIndex.get(a) as number)
        : Number.MAX_SAFE_INTEGER - 1;
      const bOrder = orderIndex.has(b)
        ? (orderIndex.get(b) as number)
        : Number.MAX_SAFE_INTEGER - 1;
      return aOrder - bOrder;
    });
    return entries;
  }, [groupedPropositions, groups]);

  if (!organization) {
    return (
      <div className="p-6">
        <p className="text-gray-600">
          Please select an organization to view propositions.
        </p>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
        <h1 className="text-2xl font-bold">Value Propositions</h1>
        {canEdit && activeTab === 'propositions' && (
          <div className="flex gap-2">
            {reorderMode ? (
              <>
                <button
                  onClick={handleCancelReorder}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300"
                  disabled={isSaving}
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveOrder}
                  className={`px-4 py-2 rounded-md ${
                    hasChanges && !isSaving
                      ? 'bg-blue-600 text-white hover:bg-blue-700'
                      : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  }`}
                  disabled={!hasChanges || isSaving}
                >
                  {isSaving ? 'Saving...' : 'Save Order'}
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={handleEnterReorderMode}
                  className="flex items-center gap-2 px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300"
                >
                  <Bars3Icon className="h-5 w-5" />
                  <span>Reorder</span>
                </button>
                <button
                  onClick={handleOpen}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                  <PlusIcon className="h-5 w-5" />
                  <span>Add Proposition</span>
                </button>
              </>
            )}
          </div>
        )}
      </div>

      {/* Unsaved changes indicator */}
      {reorderMode && hasChanges && (
        <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md flex items-center">
          <svg
            className="w-5 h-5 text-yellow-600 mr-2"
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path
              fillRule="evenodd"
              d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
              clipRule="evenodd"
            />
          </svg>
          <span className="text-sm text-yellow-800">
            You have unsaved changes. Click &quot;Save Order&quot; to save or
            &quot;Cancel&quot; to discard.
          </span>
        </div>
      )}

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('propositions')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'propositions'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            All Propositions
          </button>
          <button
            onClick={() => setActiveTab('groups')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'groups'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Groups
          </button>
        </nav>
      </div>

      {activeTab === 'propositions' ? (
        <>
          {/* Search bar */}
          {!reorderMode && (
            <div className="mb-6">
              <input
                type="text"
                placeholder="Search propositions..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full md:w-96 px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          )}

          {/* Propositions Table/Cards */}
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
            measuring={{
              droppable: {
                strategy: MeasuringStrategy.Always,
              },
            }}
          >
            <div className="hidden lg:block overflow-x-auto bg-white rounded-lg shadow">
              <table className="min-w-full">
                <thead className="bg-gray-50">
                  <tr>
                    {reorderMode && canEdit && (
                      <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"></th>
                    )}
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Proposition
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Type
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Current Capability
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Score
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Pipeline
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Metrics
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {reorderMode
                    ? // Grouped reordering: one SortableContext per group, drag within group only
                      sortedGroupEntries.map(([groupName, props]) => (
                        <React.Fragment key={`reorder-${groupName}`}>
                          {(Object.keys(groupedPropositions).length > 1 ||
                            groupName !== 'Ungrouped') && (
                            <tr className="bg-gray-50">
                              <td
                                colSpan={8}
                                className="px-6 py-2 text-sm font-semibold text-gray-700"
                              >
                                {groupName}
                              </td>
                            </tr>
                          )}
                          <SortableContext
                            items={props.map((p) => p.id)}
                            strategy={verticalListSortingStrategy}
                          >
                            {props.map((proposition) => (
                              <SortablePropositionRow
                                key={proposition.id}
                                proposition={proposition}
                                canEdit={canEdit}
                                canDelete={canDelete}
                                reorderMode={reorderMode}
                                onView={() =>
                                  navigate(`/propositions/${proposition.id}`)
                                }
                                onEdit={handleEdit}
                                onDelete={handleDelete}
                                organization={organization}
                              />
                            ))}
                          </SortableContext>
                        </React.Fragment>
                      ))
                    : // Grouped view for normal display
                      sortedGroupEntries.map(([groupName, props]) => (
                        <React.Fragment key={groupName}>
                          {(Object.keys(groupedPropositions).length > 1 ||
                            groupName !== 'Ungrouped') && (
                            <tr className="bg-gray-50">
                              <td
                                colSpan={7}
                                className="px-6 py-2 text-sm font-semibold text-gray-700"
                              >
                                {groupName}
                              </td>
                            </tr>
                          )}
                          {props.map((proposition) => (
                            <tr
                              key={proposition.id}
                              className="hover:bg-gray-50"
                            >
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div>
                                  <div className="flex items-center gap-2">
                                    {proposition.group?.color && (
                                      <div
                                        className="w-3 h-3 rounded-full flex-shrink-0"
                                        style={{
                                          backgroundColor:
                                            proposition.group.color,
                                        }}
                                      />
                                    )}
                                    <button
                                      onClick={() =>
                                        navigate(
                                          `/propositions/${proposition.id}`,
                                        )
                                      }
                                      className="text-sm font-medium text-gray-900 hover:text-blue-600 text-left"
                                    >
                                      {proposition.name}
                                    </button>
                                  </div>
                                  {proposition.owner_name && (
                                    <p className="text-xs text-gray-500 ml-5">
                                      {proposition.owner_name}
                                    </p>
                                  )}
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {formatPropositionType(proposition.type) || '-'}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {formatCurrentCapability(
                                  proposition.current_capability,
                                ) || '-'}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {proposition.obvious_choice_score !== null &&
                                proposition.obvious_choice_score !== undefined
                                  ? `${proposition.obvious_choice_score.toFixed(1)}/10`
                                  : '-'}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                <div className="space-y-1">
                                  <div>
                                    <span className="text-green-600 font-medium">
                                      {formatCurrency(
                                        proposition.booked_business || 0,
                                        organization.currency || 'USD',
                                        {
                                          compact: true,
                                        },
                                      )}
                                    </span>
                                    <span className="text-xs text-gray-500 ml-1">
                                      Won
                                    </span>
                                  </div>
                                  <div>
                                    <span className="text-blue-600 text-sm">
                                      {formatCurrency(
                                        proposition.open_pipeline_weighted || 0,
                                        organization.currency || 'USD',
                                        {
                                          compact: true,
                                        },
                                      )}
                                    </span>
                                    <span className="text-xs text-gray-500 ml-1">
                                      Weighted
                                    </span>
                                  </div>
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                <div className="flex gap-3">
                                  <div className="text-center">
                                    <div className="font-semibold">
                                      {proposition.prospect_count || 0}
                                    </div>
                                    <div className="text-xs text-gray-500">
                                      Prospects
                                    </div>
                                  </div>
                                  <div className="text-center">
                                    <div className="font-semibold">
                                      {proposition.customer_count || 0}
                                    </div>
                                    <div className="text-xs text-gray-500">
                                      Customers
                                    </div>
                                  </div>
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                <div className="flex justify-end gap-2">
                                  <button
                                    onClick={() =>
                                      navigate(
                                        `/propositions/${proposition.id}`,
                                      )
                                    }
                                    className="text-blue-600 hover:text-blue-900"
                                    title="View"
                                  >
                                    <EyeIcon className="h-5 w-5" />
                                  </button>
                                  {canEdit && (
                                    <button
                                      onClick={() => handleEdit(proposition)}
                                      className="text-yellow-600 hover:text-yellow-900"
                                      title="Edit"
                                    >
                                      <PencilIcon className="h-5 w-5" />
                                    </button>
                                  )}
                                  {canDelete && (
                                    <button
                                      onClick={() =>
                                        handleDelete(proposition.id)
                                      }
                                      className="text-red-600 hover:text-red-900"
                                      title="Delete"
                                    >
                                      <TrashIcon className="h-5 w-5" />
                                    </button>
                                  )}
                                </div>
                              </td>
                            </tr>
                          ))}
                        </React.Fragment>
                      ))}
                </tbody>
              </table>
            </div>
          </DndContext>

          {/* Mobile view - Cards (No drag-and-drop for mobile) */}
          <div className="lg:hidden space-y-4">
            {
              // Grouped view for normal display
              Object.entries(groupedPropositions).map(([groupName, props]) => (
                <div key={groupName}>
                  {groupName !== 'Ungrouped' && (
                    <h3 className="text-sm font-semibold text-gray-700 mb-2">
                      {groupName}
                    </h3>
                  )}
                  {props.map((proposition) => (
                    <div
                      key={proposition.id}
                      className="bg-white rounded-lg shadow p-4 mb-4"
                    >
                      <div className="flex justify-between items-start mb-3">
                        <div>
                          <div className="flex items-center gap-2">
                            {proposition.group?.color && (
                              <div
                                className="w-3 h-3 rounded-full flex-shrink-0"
                                style={{
                                  backgroundColor: proposition.group.color,
                                }}
                              />
                            )}
                            <button
                              onClick={() =>
                                navigate(`/propositions/${proposition.id}`)
                              }
                              className="font-semibold text-lg text-gray-900 hover:text-blue-600 text-left"
                            >
                              {proposition.name}
                            </button>
                          </div>
                          {proposition.owner_name && (
                            <p className="text-xs text-gray-500 ml-5">
                              {proposition.owner_name}
                            </p>
                          )}
                        </div>
                        {proposition.type && (
                          <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs">
                            {formatPropositionType(proposition.type)}
                          </span>
                        )}
                        {proposition.current_capability && (
                          <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded text-xs">
                            {formatCurrentCapability(
                              proposition.current_capability,
                            )}
                          </span>
                        )}
                      </div>

                      <div className="grid grid-cols-2 gap-4 mb-3">
                        <div>
                          <p className="text-xs text-gray-500">Score</p>
                          <p className="font-medium">
                            {proposition.obvious_choice_score !== null &&
                            proposition.obvious_choice_score !== undefined
                              ? `${proposition.obvious_choice_score.toFixed(1)}/10`
                              : '-'}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Won Business</p>
                          <p className="font-medium text-green-600">
                            {formatCurrency(
                              proposition.booked_business || 0,
                              organization.currency || 'USD',
                              {
                                compact: true,
                              },
                            )}
                          </p>
                        </div>
                      </div>

                      <div className="flex justify-between items-center pt-3 border-t">
                        <div className="flex gap-4 text-sm">
                          <div>
                            <span className="font-semibold">
                              {proposition.prospect_count || 0}
                            </span>
                            <span className="text-gray-500 ml-1">
                              Prospects
                            </span>
                          </div>
                          <div>
                            <span className="font-semibold">
                              {proposition.customer_count || 0}
                            </span>
                            <span className="text-gray-500 ml-1">
                              Customers
                            </span>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={() =>
                              navigate(`/propositions/${proposition.id}`)
                            }
                            className="text-blue-600 hover:text-blue-900"
                            title="View"
                          >
                            <EyeIcon className="h-5 w-5" />
                          </button>
                          {canEdit && (
                            <button
                              onClick={() => handleEdit(proposition)}
                              className="text-yellow-600 hover:text-yellow-900"
                              title="Edit"
                            >
                              <PencilIcon className="h-5 w-5" />
                            </button>
                          )}
                          {canDelete && (
                            <button
                              onClick={() => handleDelete(proposition.id)}
                              className="text-red-600 hover:text-red-900"
                              title="Delete"
                            >
                              <TrashIcon className="h-5 w-5" />
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ))
            }
          </div>

          {/* Add/Edit Dialog */}
          {open && (
            <div className="fixed inset-0 z-50 overflow-y-auto">
              <div className="flex items-center justify-center min-h-screen px-4">
                <div
                  className="fixed inset-0 bg-gray-500/75"
                  onClick={handleClose}
                ></div>

                <div className="relative bg-white rounded-lg max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto">
                  <h2 className="text-xl font-semibold mb-4">
                    {editingProposition
                      ? 'Edit Proposition'
                      : 'Add New Proposition'}
                  </h2>

                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Proposition Name *
                      </label>
                      <input
                        type="text"
                        value={formData.name}
                        onChange={(e) =>
                          setFormData({ ...formData, name: e.target.value })
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Type
                      </label>
                      <select
                        value={formData.type}
                        onChange={(e) =>
                          setFormData({ ...formData, type: e.target.value })
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">Select type</option>
                        {PROPOSITION_TYPES.map((type) => (
                          <option key={type.value} value={type.value}>
                            {type.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Description
                      </label>
                      <textarea
                        value={formData.description}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            description: e.target.value,
                          })
                        }
                        rows={3}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Current Capability
                      </label>
                      <select
                        value={formData.current_capability}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            current_capability: e.target.value,
                          })
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">Select capability level</option>
                        {CURRENT_CAPABILITIES.map((cap) => (
                          <option key={cap.value} value={cap.value}>
                            {cap.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Owner
                      </label>
                      <select
                        value={formData.owner_id}
                        onChange={(e) =>
                          setFormData({ ...formData, owner_id: e.target.value })
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">No owner</option>
                        {members.map((member) => (
                          <option key={member.user_id} value={member.user_id}>
                            {member.user_name}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Group
                      </label>
                      <select
                        value={formData.group_id || ''}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            group_id: e.target.value || null,
                          })
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">No group</option>
                        {groups.map((group) => (
                          <option key={group.id} value={group.id}>
                            {group.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="flex justify-end gap-3 mt-6">
                      <button
                        type="button"
                        onClick={handleClose}
                        className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                      >
                        {editingProposition
                          ? 'Save Changes'
                          : 'Add Proposition'}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          )}

          {/* Empty state */}
          {filteredPropositions.length === 0 && (
            <div className="text-center py-12">
              <p className="text-gray-500">
                {searchTerm
                  ? 'No propositions found matching your search.'
                  : 'No propositions yet. Add your first proposition to get started.'}
              </p>
            </div>
          )}
        </>
      ) : (
        <GroupsManagement
          organizationId={organization.id}
          groupType="proposition"
          canEdit={canEdit}
          canDelete={canDelete}
          onGroupsChange={fetchGroupsCb}
        />
      )}

      <PlanLimitDialog
        open={planLimitDialog.open}
        onClose={() => setPlanLimitDialog({ ...planLimitDialog, open: false })}
        limitType={planLimitDialog.limitType}
        currentPlan={organizationDetails?.plan_type || 'free'}
        currentCount={planLimitDialog.currentCount}
        limit={planLimitDialog.limit}
      />
    </div>
  );
}
